<template>
  <div class="cta-overlay">
    <p v-if="payload.text" class="cta-overlay__text">{{ payload.text }}</p>
    <a
      v-if="buttonText && buttonUrl"
      :href="buttonUrl"
      target="_blank"
      rel="noopener noreferrer"
      class="cta-overlay__button"
      :class="buttonVariantClass"
      :style="buttonInlineStyle"
    >
      {{ buttonText }}
    </a>
  </div>
</template>

<script setup lang="ts">
import { computed, type PropType } from 'vue'

const props = defineProps({
  payload: {
    type: Object as PropType<Record<string, unknown>>,
    default: () => ({}),
  },
})

const buttonText = computed(() => (props.payload.button_text as string) || '')
const buttonUrl = computed(() => (props.payload.button_url as string) || '')
const buttonColor = computed(() => (props.payload.button_color as string) || '')
const buttonVariant = computed(() => (props.payload.button_variant as string) || 'filled')

const buttonVariantClass = computed(() => {
  if (buttonVariant.value !== 'filled') {
    return `cta-overlay__button--${buttonVariant.value}`
  }
  return undefined
})

const buttonInlineStyle = computed(() => {
  const color = buttonColor.value
  if (!color) return undefined

  if (buttonVariant.value === 'filled') {
    return {
      background: `linear-gradient(135deg, ${color}, color-mix(in srgb, ${color} 80%, black))`,
      '--cta-glow-color': color,
    }
  }
  if (buttonVariant.value === 'tonal') {
    return {
      background: `${color}30`,
      color,
      '--cta-glow-color': color,
    }
  }
  if (buttonVariant.value === 'outlined' || buttonVariant.value === 'text') {
    return {
      color,
      '--cta-glow-color': color,
    }
  }
  return undefined
})
</script>

<style scoped lang="scss">
.cta-overlay {
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: center;
  text-align: center;
  padding-top: 16px;

  &__text {
    margin: 0;
    font-size: 15px;
    font-weight: 600;
    line-height: 1.6;
    letter-spacing: 0.01em;
    color: rgba(255, 255, 255, 0.85);
    white-space: pre-line;
  }

  &__button {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 11px 28px;
    background: linear-gradient(135deg, #6366f1, #4f46e5);
    color: #fff;
    font-size: 14px;
    font-weight: 600;
    letter-spacing: 0.02em;
    text-decoration: none;
    border-radius: 10px;
    transition: transform 200ms ease, box-shadow 200ms ease;

    &::after {
      content: '\2192';
      display: inline-block;
      font-size: 16px;
      transition: translate 200ms ease;
    }

    &:hover {
      transform: translateY(-1px);
      box-shadow:
        0 4px 16px color-mix(in srgb, var(--cta-glow-color, #6366f1) 40%, transparent),
        0 1px 3px rgba(0, 0, 0, 0.2);

      &::after {
        translate: 3px 0;
      }
    }

    &:active {
      transform: translateY(0);
    }

    &:focus-visible {
      outline: 2px solid #fff;
      outline-offset: 2px;
    }

    &--tonal {
      background: rgba(255, 255, 255, 0.1);
      border: 1px solid rgba(255, 255, 255, 0.08);
      color: #fff;

      &:hover {
        background: rgba(255, 255, 255, 0.16);
        border-color: rgba(255, 255, 255, 0.14);
        box-shadow:
          0 4px 16px rgba(255, 255, 255, 0.08),
          0 1px 3px rgba(0, 0, 0, 0.2);
      }
    }

    &--outlined {
      background: transparent;
      border: 1px solid rgba(255, 255, 255, 0.2);
      color: rgba(255, 255, 255, 0.9);

      &:hover {
        background: rgba(255, 255, 255, 0.06);
        border-color: rgba(255, 255, 255, 0.3);
        box-shadow:
          0 4px 16px color-mix(in srgb, var(--cta-glow-color, white) 12%, transparent),
          0 1px 3px rgba(0, 0, 0, 0.2);
      }
    }

    &--text {
      background: none;
      padding: 4px 0;
      color: rgba(255, 255, 255, 0.9);
      text-decoration: underline;
      text-underline-offset: 3px;
      text-decoration-color: rgba(255, 255, 255, 0.3);
      border-radius: 0;

      &:hover {
        color: #fff;
        text-decoration-color: rgba(255, 255, 255, 0.7);
        transform: none;
        box-shadow: none;
      }
    }
  }
}

// Light theme adjustments
[data-vp-theme='light'] .cta-overlay {
  &__text {
    color: rgba(0, 0, 0, 0.85);
  }

  &__button {
    &--tonal {
      background: rgba(0, 0, 0, 0.08);
      border-color: rgba(0, 0, 0, 0.06);
      color: #000000;

      &:hover {
        background: rgba(0, 0, 0, 0.12);
        border-color: rgba(0, 0, 0, 0.1);
      }
    }

    &--outlined {
      border-color: rgba(0, 0, 0, 0.2);
      color: rgba(0, 0, 0, 0.8);

      &:hover {
        background: rgba(0, 0, 0, 0.04);
        border-color: rgba(0, 0, 0, 0.3);
      }
    }

    &--text {
      color: rgba(0, 0, 0, 0.8);
      text-decoration-color: rgba(0, 0, 0, 0.3);

      &:hover {
        color: #000000;
        text-decoration-color: rgba(0, 0, 0, 0.6);
      }
    }

    &:focus-visible {
      outline-color: #000000;
    }
  }
}

@media (prefers-reduced-motion: reduce) {
  .cta-overlay__button {
    transition-duration: 1ms;

    &::after {
      transition-duration: 1ms;
    }
  }
}
</style>
